import { EntityId } from '@reduxjs/toolkit';
import { UserDTO, UserGender } from 'app/auth/store/type';
import { FilterMatch } from 'app/main/apps/listCategory/type';
import { CouponCurrencyType, CouponOutDTO } from 'app/main/design-system/models/coupon.type';
import { AcquiredFromTypeEnum, ClientType } from 'app/main/pages/clients/type';

export enum NotificationType {
  notification = 'PUSH',
  sms = 'SMS',
}

export enum LinkType {
  automatic = 'AUTOMATIC',
  personalized = 'PERSONALIZED',
}

export enum CommunicationCampaignType {
  BIRTHDAY = 'BIRTHDAY',
  LOST_CUSTOMER = 'LOST_CUSTOMER',
  NEW_CUSTOMER = 'NEW_CUSTOMER',
  MANUAL_CAMPAIGN = 'MANUAL_CAMPAIGN',
}

export enum CampaignType {
  news = 'NEWS',
  couponAmount = 'COUPON_AMOUNT',
  couponPercent = 'COUPON_PERCENT',
  couponProduct = 'COUPON_PRODUCT',
}

export enum CommunicationCampaignState {
  DRAFT = 'DRAFT',
  VALID = 'VALID',
  CANCEL = 'CANCELLED',
}

export enum CommunicationCampaignDispatchStatus {
  SCHEDULED = 'SCHEDULED',
  PENDING = 'PENDING',
  SENT = 'SENT',
  ERROR = 'ERROR',
}

export enum FilterType {
  NONE = 'NONE',
  PROFILE = 'PROFILE',
  CLIENTS = 'CLIENTS',
  CATEGORY = 'CATEGORY',
}

export interface NotificationData {
  title: string;
  message: string;
  imageUrl?: string;
  link?: Link;
  discountAmount?: number;
  discountPercentage?: number;
}

export interface CrmFieldFilter {
  fieldId: string;
  fieldLabel: string;
  values: string[];
}

export interface FranchiseProduct {
  id: string;
  name: string;
}

export interface CampaignFiltersDTO {
  storeIds: EntityId[];
  clientIds?: EntityId[];
  segmentRef?: string;
  minAge?: number;
  maxAge?: number;
  gender?: UserGender;
  minLoyaltyPoints?: number;
  maxLoyaltyPoints?: number;
  minCollectedPoints?: number;
  maxCollectedPoints?: number;
  minAverageBasket?: number;
  maxAverageBasket?: number;
  minWeeklyAverageBasket?: number;
  maxWeeklyAverageBasket?: number;
  startPassageDate?: string;
  endPassageDate?: string;
  minNbPassageTransactions?: number;
  maxNbPassageTransactions?: number;
  zipOrPostcodes: string[];
  radius: number | null;
  originProviderId: string | null;
  clientsAcquiredFrom?: AcquiredFromTypeEnum[];
  clientsTypes?: ClientType[];
  crmFieldFilters?: CrmFieldFilter[];
  consumedProductIds?: string[];
  consumedProductsMatch?: FilterMatch;
  startConsumptionDate?: string;
  endConsumptionDate?: string;
  minConsumedQuantity?: number;
  fromConnector?: boolean;
}

export interface CommunicationCampaignOutDTO {
  id?: string;
  type: NotificationType;
  name: string;
  userId: string;
  notification: NotificationData;
  campaignFilters: CampaignFiltersDTO;
  dateCreated: string;
  averageBasket?: number;
  isScheduled?: boolean;
  points?: number;
}

export interface CommunicationCampaignCreationOutDTO {
  communicationCampaign: CommunicationCampaignOutDTO;
  coupon: CouponOutDTO;
  news: NewsDTO;
  points?: number;
}

export type SmsInfo = {
  smsCost: number;
  numberSMS: number;
  singleSMSCost: number;
  numberPartialClients: number;
};

export interface CurrentCampaign {
  id?: EntityId;
  communicationCampaign: CommunicationCampaignOutDTO;
  news: NewsDTO;
  coupon?: CouponOutDTO;
  sms?: SmsInfo;
  imageUrl?: string;
  isNewsDifferentFromCampaign: boolean;
  hasCoupon: boolean;
  points?: number;
  hasLink: boolean;
  isSMSLengthValid: boolean;
  merchantUserId: string;
  startTime?: startTimeType;
  endTime?: string;
  isSent: boolean;
  isTemplate: boolean;
  isLoading?: boolean;
  hasError?: boolean;
}

export interface CommunicationCampaignInDTO {
  id: EntityId;
  name: string;
  type: NotificationType;
  startDate: string;
  endDate: string;
  dateCreated: string;
  lastModified: string;
  userId: EntityId;
  newsId?: EntityId;
  averageBasket: number;
  notification?: NotificationDTO;
  campaignFilters: CampaignFiltersDTO;
  currentCouponId?: EntityId;
  state: CommunicationCampaignState;
  dispatchStatus: CommunicationCampaignDispatchStatus;
  franchiseId: EntityId;
  isScheduled: boolean;
  points?: number;
}

export interface CommunicationCampaignDetailInDTO extends CommunicationCampaignInDTO {
  numberOfNotifiedUsers: number;
  numberOfOpenedNotifUsers: number;
}

export type AutomatedMarketingCampaignInDTO = {
  id: string;
  name: string;
  userId: string;
  userName: string;
  averageBasket?: number;
  type: NotificationType;
  campaignFilters: CampaignFiltersDTO;
  dateCreated: string;
  lastModified: string;
  startDate: string;
  endDate: string;
  notification: NotificationDTO;
  discountAmount?: number;
  discountPercentage?: number;
  automatedMarketingId?: string;
  state: CommunicationCampaignState;
  dispatchStatus: CommunicationCampaignDispatchStatus;
  news?: NewsDTO;
  isScheduled: boolean;
  points?: number;
};

export type CommunicationCampaignRevenueInDTO = {
  revenue: number;
  openedPercent: number;
  nbNotificationsSuccessfullySent: number;
  nbNotificationsSent: number;
  nbCouponsGenerated: number;
  nbConvertedUsers: number;
  news?: NewsDTO;
  conversionPercent: number;
  nbLinkClicked: number;
  currencyCode: CouponCurrencyType;
  campaignType: CampaignType;
  totalSuccessfullyNotifiedUsers: number;
  nbNotifiedUsersInvoiced: number;
  targetedUsersCount: number;
  nbLinkClickedPerWeek: number[];
} & Pick<
  AutomatedMarketingCampaignInDTO,
  | 'id'
  | 'userName'
  | 'name'
  | 'endDate'
  | 'startDate'
  | 'type'
  | 'dateCreated'
  | 'notification'
  | 'state'
  | 'dispatchStatus'
  | 'isScheduled'
>;

export interface Link {
  url: string;
  label?: string;
  isCustom: boolean;
  urlInfoId?: string;
}

export interface NotificationDTO {
  title: string;
  message: string;
  imageUrl?: string;
  link?: Link;
}

export interface NewsDTO extends NotificationDTO {
  startDate: string | Date;
  endDate: string | Date;
  dateCreated: string;
}

export type SMSCommunicationCampaignDetails = {
  nbUsers: number;
  users: UserDTO[];
  totalPrice: number;
  unitPrice: number;
  coupon?: CouponOutDTO;
  nbPartialClients?: number;
};

export type SMSCommunicationCampaignCost = {
  country: string;
  nbUsers: number;
  price: number;
};

export type startTimeType = {
  date?: string;
  hour: number;
};

export interface ClickPerWeekData {
  clicks: number[];
}

export type CampaignCouponClaim = {
  couponUserId: string;
  userId: string;
  firstName: string | null;
  lastName: string | null;
  isClientDeleted: boolean;
  claimedAt: string;
};

export type CampaignCouponClaimPage = {
  data: CampaignCouponClaim[];
  metadata: { page: number; size: number; total: number };
};
